// Proof of Play v1 — client-side event buffer.
//
// The PlayEventsBuffer records one row per playlist item that the player
// renders (image timeout, video ended, weather widget, etc.) and persists
// finalized events in IndexedDB. The player engine drains the buffer on each
// heartbeat; the Cloudflare Pages Function forwards the batch to the
// `record_play_events` RPC, which inserts into the `play_events` table.
//
// Why IndexedDB rather than memory:
//   - Survives the kiosk crashing between heartbeats.
//   - Survives temporary network outages — the buffer flushes when the next
//     heartbeat succeeds.
//
// Storage semantics:
//   - FIFO eviction at PLAY_EVENT_BUFFER_CAP (500) to bound the worst-case
//     buffer size on a screen that has been offline for days.
//   - `clientEventId` is a UUID generated when an item starts. The RPC's
//     `on conflict (player_id, client_event_id) do nothing` makes resends
//     idempotent.
//   - schedule/bundle context (scheduleState, bundleVersion) is stamped
//     onto every event at finalize time. The engine updates the buffer's
//     context whenever it swaps revisions.

import { getDb, STORE_PLAY_EVENTS } from './cache-db';
import { uuid } from './utils';
import {
  buildPlayEvent,
  type PlayEvent,
  type PlayEventEndInput,
  type PlayEventHandle,
  type PlayEventStartInput,
} from './play-event-helpers';

export type {
  PlayCompletionStatus,
  PlayItemKind,
  PlayEvent,
  PlayEventEndInput,
  PlayEventHandle,
  PlayEventStartInput,
} from './play-event-helpers';
export { buildPlayEvent } from './play-event-helpers';

const PLAY_EVENT_BUFFER_CAP = 500;
const MAX_DRAIN_BATCH = 100;

interface BufferRow extends PlayEvent {
  /** Wall-clock when finalize was called, used for FIFO ordering. */
  createdAt: number;
}

/**
 * Per-screen singleton (keyed by screenId) so PlayerStage and PlayerEngine
 * share the same buffer without prop-drilling. Construct via
 * `getPlayEventsBuffer(screenId)`; the engine sets schedule/bundle context
 * via `setContext`.
 */
export class PlayEventsBuffer {
  private scheduleState: string | null = null;
  private bundleVersion: string | null = null;
  private droppedCount = 0;
  /**
   * Per-player Proof-of-Play toggle. Defaults to true until the first
   * heartbeat tells us otherwise — that preserves existing behaviour and
   * means we never silently swallow events on boot. When false, `recordEnd`
   * skips persisting. `drain` keeps working so events that were already in
   * the buffer when the toggle flipped flow out and get cleared locally
   * (the server rejects the batch).
   */
  private enabled = true;

  constructor(public readonly screenId: string) {}

  /** Toggle event persistence at runtime. Called by the engine on each heartbeat. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Update the schedule/bundle context tagged on subsequent events.
   * Called by PlayerEngine on every revision activation and heartbeat sync.
   */
  setContext(ctx: { scheduleState?: string | null; bundleVersion?: string | null }): void {
    if (ctx.scheduleState !== undefined) this.scheduleState = ctx.scheduleState;
    if (ctx.bundleVersion !== undefined) this.bundleVersion = ctx.bundleVersion;
  }

  recordStart(input: PlayEventStartInput): PlayEventHandle {
    return {
      clientEventId: uuid(),
      startedAt: Date.now(),
      scheduledDurationMs: Math.max(0, input.scheduledDurationMs),
    };
  }

  async recordEnd(
    handle: PlayEventHandle,
    start: PlayEventStartInput,
    end: PlayEventEndInput,
  ): Promise<void> {
    if (!this.enabled) return;
    const row: BufferRow = {
      ...buildPlayEvent(handle, start, end, {
        screenId: this.screenId,
        scheduleState: this.scheduleState,
        bundleVersion: this.bundleVersion,
      }),
      createdAt: Date.now(),
    };
    await this.persist(row);
  }

  /** Pop up to `max` oldest events for transmission. */
  async drain(max: number = MAX_DRAIN_BATCH): Promise<PlayEvent[]> {
    const cap = Math.max(1, Math.min(MAX_DRAIN_BATCH, max));
    const db = await getDb();
    const tx = db.transaction(STORE_PLAY_EVENTS, 'readonly');
    const idx = tx.store.index('byCreatedAt');
    const out: PlayEvent[] = [];
    let cursor = await idx.openCursor();
    while (cursor && out.length < cap) {
      const row = cursor.value as BufferRow;
      if (row.screenId === this.screenId) {
        out.push(toWire(row));
      }
      cursor = await cursor.continue();
    }
    await tx.done;
    return out;
  }

  /** Delete acknowledged events from the buffer. */
  async ack(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const db = await getDb();
    const tx = db.transaction(STORE_PLAY_EVENTS, 'readwrite');
    await Promise.all(ids.map((id) => tx.store.delete(id)));
    await tx.done;
  }

  /** Number of events FIFO-dropped on overflow since process start. */
  getDroppedCount(): number {
    return this.droppedCount;
  }

  /** Reset the dropped counter after surfacing it via heartbeat. */
  consumeDroppedCount(): number {
    const n = this.droppedCount;
    this.droppedCount = 0;
    return n;
  }

  /** Test-only: clear all buffered events for this screen. */
  async clearForTests(): Promise<void> {
    const db = await getDb();
    const tx = db.transaction(STORE_PLAY_EVENTS, 'readwrite');
    let cursor = await tx.store.openCursor();
    while (cursor) {
      const row = cursor.value as BufferRow;
      if (row.screenId === this.screenId) await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
    this.droppedCount = 0;
  }

  private async persist(row: BufferRow): Promise<void> {
    const db = await getDb();
    const tx = db.transaction(STORE_PLAY_EVENTS, 'readwrite');
    const count = await tx.store.count();
    if (count >= PLAY_EVENT_BUFFER_CAP) {
      const idx = tx.store.index('byCreatedAt');
      const cursor = await idx.openCursor();
      // Drop the single oldest row, regardless of screenId. A multi-screen
      // kiosk is not a supported deployment; this keeps the eviction O(1)
      // instead of scanning the index.
      if (cursor) {
        await cursor.delete();
        this.droppedCount += 1;
      }
    }
    await tx.store.put(row);
    await tx.done;
  }
}

const buffers = new Map<string, PlayEventsBuffer>();

/** Per-screen singleton. */
export function getPlayEventsBuffer(screenId: string): PlayEventsBuffer {
  let b = buffers.get(screenId);
  if (!b) {
    b = new PlayEventsBuffer(screenId);
    buffers.set(screenId, b);
  }
  return b;
}

function toWire(row: BufferRow): PlayEvent {
  const { createdAt: _ignored, ...wire } = row;
  void _ignored;
  return wire;
}

// Exposed for tests.
export const __TEST = {
  PLAY_EVENT_BUFFER_CAP,
  MAX_DRAIN_BATCH,
};
